/**
 * slideSelection.ts — resolve which slides the user is acting on.
 *
 * The transform request is keyed by 0-based presentation position (matching the
 * package's slide order), but the write-back has to survive slides being
 * inserted and deleted, so the selection carries the host's stable slide id too.
 */

/**
 * PowerPoint.Slide exposes `id` at runtime but the shipped typings omit it, so
 * it is narrowed here rather than reaching for `any`.
 */
export type SlideWithId = { id: string; load(properties: string): void };

export interface SelectedSlide {
  /** 0-based position in presentation order. */
  readonly index: number;
  /** Host-stable slide id, used to re-find the slide after the deck mutates. */
  readonly id: string;
}

/** Thrown when the host reports no usable slide selection. */
export class NoSlideSelectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NoSlideSelectedError";
  }
}

const SELECTION_EMPTY_MESSAGE = "Select at least one slide to redesign.";

/**
 * The currently selected slides, in presentation order.
 *
 * @throws {NoSlideSelectedError} when nothing is selected or the selection
 * cannot be matched against the deck.
 */
export async function getSelectedSlides(): Promise<SelectedSlide[]> {
  return PowerPoint.run(async (context) => {
    const allSlides = context.presentation.slides;
    allSlides.load("items");
    const selection = context.presentation.getSelectedSlides();
    selection.load("items");
    await context.sync();

    if (selection.items.length === 0) throw new NoSlideSelectedError(SELECTION_EMPTY_MESSAGE);

    for (const slide of allSlides.items) (slide as unknown as SlideWithId).load("id");
    for (const slide of selection.items) (slide as unknown as SlideWithId).load("id");
    await context.sync();

    // Position is resolved by id match rather than by the selection's own order,
    // which the host does not guarantee to be presentation order.
    const positionById = new Map<string, number>();
    allSlides.items.forEach((slide, index) => {
      positionById.set((slide as unknown as SlideWithId).id, index);
    });

    const selected: SelectedSlide[] = [];
    for (const slide of selection.items) {
      const id = (slide as unknown as SlideWithId).id;
      const index = positionById.get(id);
      if (index !== undefined) selected.push({ index, id });
    }

    if (selected.length === 0) throw new NoSlideSelectedError(SELECTION_EMPTY_MESSAGE);
    return selected.sort((a, b) => a.index - b.index);
  });
}
